/**
 * 入场音效：心跳的“咚—哒”和爆炸声，全部用 Web Audio 实时合成，没有音频文件。做法同原型。
 * 浏览器只允许在用户点过之后出声，所以要先 unlock()；没点过的话所有调用都静默。
 * 背景音乐（可开关、可换歌）以后单独移植。
 */

export interface Sound {
  /** 在点击、按键的回调里调用；返回能不能出声 */
  unlock(): boolean;
  /** 一次心跳：period 秒内先强后弱两下 */
  heartbeat(period: number, strength?: number): void;
  boom(): void;
}

export function createSound(): Sound {
  let ctx: AudioContext | null = null;
  let sfx: GainNode | null = null;
  let reverb: ConvolverNode | null = null;

  const hz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
  const live = () => ctx !== null && ctx.state === 'running' && sfx !== null;

  function ensure(): AudioContext | null {
    if (ctx) return ctx;
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
    sfx = ctx.createGain();
    sfx.gain.value = 0.85;
    sfx.connect(master);
    // 程序生成的混响
    const len = Math.floor(ctx.sampleRate * 3.4), ir = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = ir.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
    }
    reverb = ctx.createConvolver();
    reverb.buffer = ir;
    const wet = ctx.createGain();
    wet.gain.value = 0.5;
    reverb.connect(wet);
    wet.connect(master);
    return ctx;
  }

  function thump(t: number, k: number): void {
    const c = ctx!, o = c.createOscillator(), g = c.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(78, t);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.16);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(k, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.26);
    o.connect(g);
    g.connect(sfx!);
    o.start(t);
    o.stop(t + 0.3);
  }

  function chime(f: number, t: number, pan: number): void {
    const c = ctx!, o = c.createOscillator(), g = c.createGain();
    o.type = 'sine';
    o.frequency.value = f;
    let out: AudioNode = g;
    if (c.createStereoPanner) {
      const p = c.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      out = p;
    }
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.04, t + 0.005);
    g.gain.setValueAtTime(0.04, t + 0.055);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.66);
    o.connect(g);
    out.connect(sfx!);
    out.connect(reverb!);
    o.start(t);
    o.stop(t + 1.75);
  }

  return {
    unlock() {
      const c = ensure();
      if (!c) return false;
      void c.resume();
      return true;
    },
    heartbeat(period, strength = 1) {
      if (!live()) return;
      const t = ctx!.currentTime + 0.02;
      thump(t + period * 0.04, 0.55 * strength);
      thump(t + period * 0.26, 0.32 * strength);
    },
    boom() {
      if (!live()) return;
      const c = ctx!, t = c.currentTime + 0.02;
      const len = Math.floor(c.sampleRate * 2.4), nb = c.createBuffer(1, len, c.sampleRate), d = nb.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.2);
      const n = c.createBufferSource(), lp = c.createBiquadFilter(), g = c.createGain();
      n.buffer = nb;
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(2600, t);
      lp.frequency.exponentialRampToValueAtTime(90, t + 1.8);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.9, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 2.3);
      n.connect(lp);
      lp.connect(g);
      g.connect(sfx!);
      n.start(t);
      n.stop(t + 2.4);
      const o = c.createOscillator(), og = c.createGain();
      o.frequency.setValueAtTime(64, t);
      o.frequency.exponentialRampToValueAtTime(26, t + 1.4);
      og.gain.setValueAtTime(0.0001, t);
      og.gain.exponentialRampToValueAtTime(1, t + 0.02);
      og.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
      o.connect(og);
      og.connect(sfx!);
      o.start(t);
      o.stop(t + 2);
      // 一串上行的亮音，像星星被点亮
      [84, 88, 91, 96].forEach((m, k) => chime(hz(m), t + 0.5 + k * 0.16, (k - 1.5) * 0.3));
    },
  };
}
